import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Download, Lock, Award, FileText, ShoppingCart, CheckCircle2 } from "lucide-react";
import { jsPDF } from "jspdf";

const W = 1600, H = 1130;

export default function Certificate() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const [buying, setBuying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [fullName, setFullName] = useState("");
  const canvasRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/certificate");
      setData(r.data);
      if (!r.data.paid && r.data.name) setFullName((prev) => prev || r.data.name);
      return r.data;
    }
    catch { setData({ eligible: false }); return null; }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe Checkout: verify payment SERVER-SIDE before unlocking.
  useEffect(() => {
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.message(t("cert.canceled"));
      setParams({}, { replace: true });
      return;
    }
    if (!sessionId) return;
    let tries = 0;
    setVerifying(true);
    const poll = async () => {
      tries += 1;
      try {
        const { data: st } = await api.get(`/payments/status/${sessionId}`);
        if (st.payment_status === "paid") {
          setVerifying(false);
          await load();
          toast.success(t("cert.paid_ok"));
          setParams({}, { replace: true });
          return;
        }
        if (["failed", "expired"].includes(st.payment_status) || tries > 8) {
          setVerifying(false);
          setParams({}, { replace: true });
          return;
        }
      } catch {
        if (tries > 8) { setVerifying(false); return; }
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params]); // eslint-disable-line

  const dateStr = () => {
    const d = data?.issued_at ? new Date(data.issued_at) : new Date();
    const locale = lang === "de" ? "de-DE" : lang === "ro" ? "ro-RO" : "en-GB";
    return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
  };

  // Draw the certificate ONLY once eligible AND paid (design unchanged).
  useEffect(() => {
    if (!data?.eligible || !data?.paid || !canvasRef.current) return;
    const draw = async () => {
      try { await document.fonts.load("400 90px 'Cormorant Garamond'"); await document.fonts.ready; } catch {}
      const ctx = canvasRef.current.getContext("2d");
      ctx.fillStyle = "#050505"; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "#D4AF37"; ctx.lineWidth = 2; ctx.strokeRect(50, 50, W - 100, H - 100);
      ctx.strokeStyle = "rgba(212,175,55,0.35)"; ctx.lineWidth = 1; ctx.strokeRect(68, 68, W - 136, H - 136);
      ctx.textAlign = "center";
      ctx.fillStyle = "#D4AF37"; ctx.font = "600 30px Inter, sans-serif";
      ctx.fillText("A I   L E R N W E R K", W / 2, 190);
      ctx.fillStyle = "#F0EBE0"; ctx.font = "500 82px 'Cormorant Garamond', serif";
      ctx.fillText(t("cert.title"), W / 2, 320);
      ctx.fillStyle = "#A3A3A3"; ctx.font = "300 26px Inter, sans-serif";
      ctx.fillText(t("cert.awarded").toUpperCase(), W / 2, 430);
      ctx.fillStyle = "#F0EBE0"; ctx.font = "500 96px 'Cormorant Garamond', serif";
      ctx.fillText(data.name || "—", W / 2, 540);
      ctx.strokeStyle = "#D4AF37"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(W / 2 - 120, 590); ctx.lineTo(W / 2 + 120, 590); ctx.stroke();
      ctx.fillStyle = "#A3A3A3"; ctx.font = "300 30px Inter, sans-serif";
      ctx.fillText(t("cert.subtitle"), W / 2, 680);
      ctx.font = "300 24px Inter, sans-serif";
      ctx.fillText(t("cert.line"), W / 2, 730);
      ctx.fillStyle = "#D4AF37"; ctx.font = "400 28px 'JetBrains Mono', monospace";
      ctx.fillText(dateStr(), W / 2, 900);
      ctx.fillStyle = "#737373"; ctx.font = "300 20px Inter, sans-serif";
      ctx.fillText("minijobai.com", W / 2, 950);
    };
    draw();
  }, [data, lang]); // eslint-disable-line

  const download = () => {
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url; a.download = "AI-Lernwerk-Zertifikat.png"; a.click();
  };

  const downloadPdf = () => {
    try {
      const imgData = canvasRef.current.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [W, H] });
      pdf.addImage(imgData, "PNG", 0, 0, W, H);
      pdf.save("AI-Lernwerk-Zertifikat.pdf");
    } catch { toast.error("Error"); }
  };

  const buy = async () => {
    const name = fullName.trim();
    if (!name) { toast.error(t("cert.name_required")); return; }
    setBuying(true);
    try {
      const { data: res } = await api.post("/payments/certificate/checkout", { origin_url: window.location.origin, full_name: name });
      if (res.already_paid) { await load(); setBuying(false); return; }
      window.location.href = res.checkout_url;
    } catch (err) {
      setBuying(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-void"><Navbar />
        <div className="pt-40 text-center text-ink-soft">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-16 max-w-4xl mx-auto px-6" data-testid="certificate-page">
        <h1 className="font-serif text-5xl text-ink mb-4 flex items-center gap-3">
          <Award className="text-gold" strokeWidth={1.5} /> {t("cert.title")}
        </h1>

        {!data.eligible ? (
          <div className="bg-graphite border border-ink/5 p-10 text-center" data-testid="certificate-locked">
            <Lock className="text-ink-muted mx-auto mb-4" size={40} strokeWidth={1.2} />
            <p className="text-ink-soft text-lg mb-4">{t("cert.locked")}</p>
            <p className="text-gold font-serif text-3xl mb-6">
              {(data.total || 0) - (data.completed_count || 0)} <span className="text-ink-muted text-lg">{t("cert.remaining")}</span>
            </p>
            <Link to="/course" className="inline-block bg-gold text-void px-6 py-3 hover:bg-gold-hover transition-colors">
              {t("nav.course")}
            </Link>
          </div>
        ) : !data.paid ? (
          <div data-testid="certificate-purchase">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="text-gold" strokeWidth={1.5} size={26} />
              <h2 className="font-serif text-3xl text-ink">{t("cert.finished_title")}</h2>
            </div>
            <p className="text-ink-soft mb-8">{t("cert.finished_sub")}</p>

            <div className="bg-graphite border border-gold/20 p-8">
              <p className="text-gold text-sm tracking-[0.2em] uppercase mb-4">{t("cert.free_note")}</p>
              <p className="text-ink-soft leading-relaxed mb-2">{t("cert.buy_line")}</p>
              <p className="text-ink-muted text-sm mb-6">{t("cert.not_accredited")}</p>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-serif text-4xl text-ink">{data.price || "10,00 €"}</span>
                <span className="text-ink-muted text-sm">· {lang === "de" ? "einmalig" : lang === "ro" ? "plată unică" : "one-time"}</span>
              </div>
              <div className="mb-6">
                <label htmlFor="cert-full-name" className="block text-ink-soft text-sm mb-2">{t("cert.name_label")}</label>
                <input id="cert-full-name" type="text" value={fullName} maxLength={80}
                  onChange={(e) => setFullName(e.target.value)} placeholder={t("cert.name_ph")}
                  data-testid="certificate-name-input"
                  className="w-full max-w-md bg-void border border-ink/15 text-ink px-4 py-3 focus:border-gold/50 outline-none" />
              </div>
              <button onClick={buy} disabled={buying || verifying || !fullName.trim()} data-testid="certificate-buy"
                className="bg-gold text-void px-8 py-4 font-medium hover:bg-gold-hover transition-colors inline-flex items-center gap-2 disabled:opacity-50">
                <ShoppingCart size={18} strokeWidth={1.5} />
                {verifying ? t("cert.verifying") : buying ? t("cert.buying") : t("cert.buy_btn")}
              </button>
              {verifying && <p className="text-ink-muted text-sm mt-4" data-testid="certificate-verifying">{t("cert.verifying")}</p>}
            </div>
          </div>
        ) : (
          <>
            <p className="text-gold text-xl font-serif mb-6" data-testid="certificate-congrats">{t("cert.congrats")}</p>
            <div className="border border-gold/20 overflow-hidden mb-4">
              <canvas ref={canvasRef} width={W} height={H} className="w-full h-auto block" data-testid="certificate-canvas" />
            </div>
            <p className="text-ink-muted text-sm mb-6">{t("cert.not_accredited")}</p>
            <div className="flex flex-wrap gap-4">
              <button onClick={download} data-testid="certificate-download"
                className="bg-gold text-void px-8 py-3 font-medium hover:bg-gold-hover transition-colors inline-flex items-center gap-2">
                <Download size={18} strokeWidth={1.5} /> {t("cert.download")}
              </button>
              <button onClick={downloadPdf} data-testid="certificate-download-pdf"
                className="border border-gold/40 text-gold px-8 py-3 font-medium hover:bg-gold hover:text-void transition-colors inline-flex items-center gap-2">
                <FileText size={18} strokeWidth={1.5} /> {t("cert.download_pdf")}
              </button>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
